'use client'

import { useEffect, type CSSProperties } from 'react'
import { cn } from '@/utils/tailwind/utils'

type ToastVariant = 'success' | 'error' | 'info'

type ToastProps = {
  open: boolean
  message: string
  onClose?: () => void
  durationMs?: number
  variant?: ToastVariant
  className?: string
  style?: CSSProperties
}

const variantClasses: Record<ToastVariant, string> = {
  success: 'border-green-200 bg-green-50 text-green-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  info: 'border-gray-200 bg-white text-gray-900',
}

export default function Toast({
  open,
  message,
  onClose,
  durationMs = 1800,
  variant = 'info',
  className,
  style,
}: ToastProps) {
  useEffect(() => {
    if (!open || !onClose) {
      return
    }

    const timerId = window.setTimeout(() => {
      onClose()
    }, durationMs)

    return () => window.clearTimeout(timerId)
  }, [open, onClose, durationMs])

  return (
    <div
      className={cn(
        'pointer-events-none fixed right-6 top-20 z-50 max-w-[calc(100dvw-3rem)] rounded-md border px-8 py-3 text-sm shadow-lg transition-all duration-200',
        open ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        variantClasses[variant],
        className,
      )}
      style={style}
      role="status"
      aria-live="polite"
      aria-hidden={!open}>
      {message}
    </div>
  )
}
